import * as React from "react";
import * as StateLib from "@/Utils/StateLib";
import { useBlocker } from "react-router-dom";

import { Text, Tabs, DialogContent, Flex } from "@theorgchartdev/storybook";
import {
  AccountSettingSectionNames,
  AccountSettingsSectionRoutes,
  enumAccountSettings,
  getAccountSettingsTestPrefix
} from "@/providers/NavigationProvider";
import { ChartManagement } from "./ChartManagement/ChartManagement";
import { ManageUsers } from "./ManageUsers/ManageUsers";
import { GeneralConfiguration } from "./General/GeneralConfiguration";
import { ThemeConfiguration } from "./ThemeConfiguration/ThemeConfiguration";
import { AccessGroups } from "./AccessGroups/AccessGroups";
import UpgradeAccessGroups from "./AccessGroups/UpgradeAccessGroups";
import { Checkpoints } from "./Checkpoints/Checkpoints";
import cx from "classnames";
import { SettingsModalAction } from "../SettingsModal";
import { UIProfiles } from "./UIProfiles/UIProfiles";
import UpgradeUIProfiles from "./UIProfiles/UpgradeUIProfiles";
import { AuthorizationConfiguration } from "./Authorization/Authorization";
import { SecurityConfiguration } from "./Security/Security";
import { AuditLog } from "./AuditLog/AuditLog";
import UpgradeAuditLog from "./AuditLog/UpgradeAuditLog";
import UpgradeTabHeader from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeTabHeader";
import { enumFeaturesList, kAdminUser } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";

import {
  sanitizeBaseChartList,
  useAccountSettingsContext
} from "@/providers/AccountSettingsProvider";
import { useEffect } from "react";
import { getFeatureAccess } from "@/Utils/Misc";

const AccountSettings = ({ currentTab, setCurrentTab, onCloseModal }) => {
  if (!(Globals._accountInfo.GetUserRole() === kAdminUser)) return;

  const [changedSections, setChangedSections] = React.useState([]);
  const {
    accountConfig,
    accountConfigRef,
    clonedAccountConfig,
    updateAccountConfigRef,
    setAccountConfig,
    SaveAccountConfigChanges,
    ReportLaunchAccountSettingsEvent
  } = useAccountSettingsContext();

  accountConfigRef.current = accountConfig;

  useEffect(() => {
    ReportLaunchAccountSettingsEvent();
  }, []);

  const GetChangedConfigData = () => {
    let data = {};
    for (let section of changedSections) {
      data[section] = accountConfigRef.current[section];
    }
    if (data.charts) {
      data = sanitizeBaseChartList(data);
    }
    return data;
  };

  const SaveSettings = async (nextSection, closeAfterSave) => {
    // save guard against bad state
    let data = GetChangedConfigData();
    await SaveAccountConfigChanges(data, {
      nextSection,
      closeAfterSave,
      handleTabChange,
      onCloseModal,
      setChangedSections
    });
  };

  const UpdateAccountConfig = (updatedAccountConfig, key) => {
    const nextConfig = { ...accountConfig, ...updatedAccountConfig };
    updateAccountConfigRef(nextConfig);
    if (key && key.length > 0 && !changedSections.includes(key))
      setChangedSections(prevChangedSections => [...prevChangedSections, key]);
  };

  const ResetAccountConfig = (newSection, exit) => {
    setChangedSections([]);
    setAccountConfig(clonedAccountConfig);
    if (newSection != null) {
      handleTabChange(newSection);
    }
    if (exit) onCloseModal();
  };

  const PromptToSave = (changeSection, exit, onPostPrompt, onDismiss) => {
    const wsMgr = StateLib.GetWSMgr();
    var header = appstr("Save Changes");
    var message = `${appstr("saveconfirm1")} ${appstr(GetCurrentTabName())}${appstr("saveconfirm2")}`;
    wsMgr.CreateReactAlert(
      header,
      message,
      () => {
        SaveSettings(changeSection, exit);
        onPostPrompt?.();
      },
      () => {
        ResetAccountConfig(changeSection, exit);
        onPostPrompt?.();
      },
      header,
      appstr("Discard"),
      onDismiss
    );
  };

  const IsDirty = () => {
    return changedSections.length > 0;
  };

  const blocker = useBlocker(IsDirty);

  React.useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }

    PromptToSave(null, false, blocker.proceed, blocker.reset);
  }, [blocker.state]);

  const GetCurrentTabName = () => {
    return AccountSettingSectionNames[enumAccountSettings[currentTab]];
  };

  const handleTabChange = currentTabIndex => {
    setCurrentTab(currentTabIndex);
  };

  const GetTabContent = section => {
    let key = AccountSettingSectionNames[section];
    let sectionTab = {};
    sectionTab.header = appstr(key);
    sectionTab.dataTestId = `account-settings-${section}`;
    let content = null;
    switch (key) {
      case AccountSettingSectionNames.General:
        content = (
          <GeneralConfiguration accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />
        );
        break;
      case AccountSettingSectionNames.AccessGroups:
        if (getFeatureAccess(enumFeaturesList.accessGroups)) {
          content = (
            <AccessGroups accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />
          );
        } else {
          content = <UpgradeAccessGroups />;
          sectionTab.header = (
            <UpgradeTabHeader label={appstr(key)} dataTestIdPrefix={sectionTab.dataTestId} />
          );
        }
        break;
      case AccountSettingSectionNames.ManageUsers:
        content = <ManageUsers accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />;
        break;
      case AccountSettingSectionNames.UIProfiles:
        if (getFeatureAccess(enumFeaturesList.uiProfiles)) {
          content = <UIProfiles accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />;
        } else {
          content = <UpgradeUIProfiles />;
          sectionTab.header = (
            <UpgradeTabHeader label={appstr(key)} dataTestIdPrefix={sectionTab.dataTestId} />
          );
        }
        break;
      case AccountSettingSectionNames.BaseCharts:
        content = (
          <ChartManagement accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />
        );
        break;
      case AccountSettingSectionNames.Theme:
        content = (
          <ThemeConfiguration accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />
        );
        break;
      case AccountSettingSectionNames.Checkpoints:
        content = <Checkpoints accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />;
        break;
      case AccountSettingSectionNames.Authorization:
        content = (
          <AuthorizationConfiguration
            accountConfig={accountConfig}
            handleUpdate={UpdateAccountConfig}
          />
        );
        break;
      case AccountSettingSectionNames.Security:
        content = (
          <SecurityConfiguration accountConfig={accountConfig} handleUpdate={UpdateAccountConfig} />
        );
        break;
      case AccountSettingSectionNames.AuditLog:
        if (getFeatureAccess(enumFeaturesList.adminLog)) {
          content = <AuditLog />;
        } else {
          content = <UpgradeAuditLog />;
          sectionTab.header = (
            <UpgradeTabHeader label={appstr(key)} dataTestIdPrefix={sectionTab.dataTestId} />
          );
        }
        break;
      default:
        content = (
          <Text size={"medium"} modifier={"medium"} colorModifier={"grey"}>
            {appstr("Area under construction")}
          </Text>
        );
        break;
    }
    sectionTab.content = (
      <div className={cx("ocn-settings-content", "ocn-list-scrollbar")}>{content}</div>
    );
    return sectionTab;
  };

  const GetTabs = () => {
    let tabList = [];
    for (let section in AccountSettingSectionNames) {
      tabList.push(GetTabContent(section));
    }
    return tabList;
  };

  const tabs = GetTabs();

  const currentTabSlug =
    AccountSettingsSectionRoutes[Object.keys(AccountSettingSectionNames)[parseInt(currentTab)]];
  const currentTabTestPrefix = getAccountSettingsTestPrefix(currentTabSlug);

  let saveButtonProps = {
    onClick: () => SaveSettings(),
    disabled: !IsDirty(),
    dataTestId: `${currentTabTestPrefix}-save-button`
  };

  let resetButtonProps = {
    onClick: () => ResetAccountConfig(),
    disabled: !IsDirty(),
    dataTestId: `${currentTabTestPrefix}-reset-button`
  };

  let cancelButtonProps = {
    onClick: onCloseModal,
    dataTestId: `${currentTabTestPrefix}-cancel-button`
  };

  return (
    <React.Fragment>
      <Flex direction={"column"} height={"100%"}>
        <DialogContent customContentClass={"ocn-settings-container"}>
          <div className={"ocn-settings-content-container"}>
            <Tabs
              tabIndex={parseInt(currentTab)}
              customClass={"ocn-settings-tabs"}
              customContentClass={"ocn-settings-tabs-content"}
              tabs={tabs}
              onTabChange={handleTabChange}
            />
          </div>
        </DialogContent>
        <SettingsModalAction
          saveButtonProps={saveButtonProps}
          resetButtonProps={resetButtonProps}
          cancelButtonProps={cancelButtonProps}
        />
      </Flex>
    </React.Fragment>
  );
};

export default AccountSettings;
